import { RepairOrderRequestLine, WorkInProgressLine } from "@/services/api";
import * as React from "react";
import { IColumnDefinition, PCSTable } from "../PCSTable";
import { Label } from "@fluentui/react-label";

export interface IWorkInProgressLineTableProps {
	lines: WorkInProgressLine[];
	selectedLinesIds?: string[];
	onSelectionChange?: (selectedLines: RepairOrderRequestLine[]) => void;
	disableSelection?: boolean;
	maxHeight?: number;
}

export function WorkInProgressLineTable(props: IWorkInProgressLineTableProps) {
	const lines = React.useMemo(() => {
		const linesOut = [...props.lines];
		linesOut.sort((a, b) => a.line! - b.line!);
		return linesOut;
	}, [props.lines]);

	const columns: IColumnDefinition<WorkInProgressLine>[] = React.useMemo<
		IColumnDefinition<WorkInProgressLine>[]
	>(() => {
		return [
			{
				columnId: "lineNumber",
				headerText: "Line",
				getValue(item) {
					return item.line;
				},
				sizing: {
					idealWidth: 35,
					minWidth: 35,
				},
			},
			{
				columnId: "oper",
				headerText: "Oper",
				getValue: (item) => item.operation,
			},
			{
				columnId: "description",
				headerText: "Description",
				sizing: {
					idealWidth: 400,
					minWidth: 400,
				},
				onRender(item) {
					const isHeader = isHeaderRow(item);

					return isHeader ? (
						<Label weight="semibold" onClick={() => console.log(item)}>
							{item.description}
						</Label>
					) : (
						<span style={{ paddingLeft: "10px" }} onClick={() => console.log(item)}>
							{item.description}
						</span>
					);
				},
			},
			{
				columnId: "partNumber",
				headerText: "Part #",
				getValue: (item) => item.partNumber,
			},
			{
				columnId: "qty",
				headerText: "Qty",
				getValue: (item) => item.quantity,
			},
			{
				columnId: "exPrice",
				headerText: "Extended Price $",
				getValue: (item) => item.extendedPrice,
			},
			{
				columnId: "labor",
				headerText: "Labor",
				getValue: (item) => item.labor,
			},
			{
				columnId: "laborType",
				headerText: "Labor Type",
				getValue: (item) => item.laborType?.split("").pop(),
			},
		];
	}, []);

	return (
		<div>
			<PCSTable
				getRowId={(item) => {
					return item.line!;
				}}
				canSelectRow={(item) => {
					const isHeader = isHeaderRow(item);
					return !isHeader;
				}}
				getRowClass={(item) => (isHeaderRow(item) && "is-header-row") || ""}
				columns={columns}
				selectionMode="multiselect"
				selectOnRowClick
				disableSelection={props.disableSelection}
				stickyHeader
				maxHeight={props.maxHeight}
				onSelectionChange={(selection) => {
					//console.log(selection);

					if (props.onSelectionChange) {
						const selectedLines: RepairOrderRequestLine[] = Array.from(selection)
							.map<RepairOrderRequestLine | null>((i) => {
								const line = lines.find((j) => j.line === i);
								if (!line) return null;
								if (isHeaderRow(line)) return null;
								const headers = lines.filter((j) => j.line! < (i as number) && isHeaderRow(j));
								return {
									line: line.line,
									description: line.description,
									extendedPrice: line.extendedPrice,
									labor: line.labor,
									laborIncluded: line.laborIncluded,
									laborType: line.laborType,
									operation: line.operation,
									partNumber: line.partNumber,
									partType: line.partType,
									quantity: line.quantity,
									uniqueSequenceNum: line.uniqueSequenceNum,
									sectionHeader: headers[headers.length - 1]?.description,
								};
							})
							.filter((i) => !!i) as RepairOrderRequestLine[];
						props.onSelectionChange(selectedLines);
					}
				}}
				items={lines}></PCSTable>
		</div>
	);
}

function isHeaderRow(line: WorkInProgressLine) {
	const isHeader = (line.labor ?? null) === null && !line.extendedPrice;
	return isHeader;
}
